'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

const supabase = createClient();

interface Piano {
  id: string;
  nome: string;
  slug: string;
  descrizione: string;
  prezzo_mensile: number;
  prezzo_annuale: number;
  valuta: string;
  funzionalita: string[];
  evidenziato: boolean;
  max_dispositivi: number | null;
  max_utenti: number | null;
}

export default function PrezziContent() {
  const [piani, setPiani] = useState<Piano[]>([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState<'mensile' | 'annuale'>('mensile');
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPiano, setSelectedPiano] = useState<Piano | null>(null);
  const [checkoutForm, setCheckoutForm] = useState({ nome: '', email: '', struttura: '', telefono: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    supabase.from('piani_prezzi').select('*').eq('attivo', true).order('ordine').then(({ data }) => {
      setPiani(data || []);
      setLoading(false);
    });
  }, []);

  const handleSelectPiano = (piano: Piano) => {
    if (piano.slug === 'base') return;
    setSelectedPiano(piano);
    setShowCheckout(true);
  };

  const handleCheckout = async () => {
    if (!checkoutForm.nome || !checkoutForm.email) return;
    setSubmitting(true);
    try {
      if (selectedPiano?.slug === 'enterprise') {
        // For Enterprise: save lead and redirect to contact
        await supabase.from('abbonamenti').insert({
          piano_id: selectedPiano.id,
          stato: 'trial',
          periodo,
          importo: periodo === 'mensile' ? selectedPiano.prezzo_mensile : selectedPiano.prezzo_annuale,
          metadati: { ...checkoutForm, richiesta_enterprise: true },
        });
        setSuccess(true);
      } else {
        // For Struttura: initiate Stripe checkout
        const { data: { user } } = await supabase.auth.getUser();
        const res = await fetch('/api/stripe/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            piano_slug: selectedPiano?.slug,
            periodo,
            user_id: user?.id,
            email: checkoutForm.email,
            nome: checkoutForm.nome,
            struttura: checkoutForm.struttura,
          }),
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          setSuccess(true);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrezzo = (piano: Piano) => {
    if (piano.slug === 'base') return 'Gratuito';
    const prezzo = periodo === 'mensile' ? piano.prezzo_mensile : piano.prezzo_annuale / 12;
    return `€${prezzo.toFixed(0)}/mese`;
  };

  const risparmio = (piano: Piano) => {
    if (piano.slug === 'base') return null;
    const mensile = piano.prezzo_mensile * 12;
    const annuale = piano.prezzo_annuale;
    if (mensile <= 0 || annuale <= 0) return null;
    const pct = Math.round(((mensile - annuale) / mensile) * 100);
    return pct > 0 ? pct : null;
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      {/* Hero */}
      <div className="text-center py-16 px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Piani e Prezzi
        </h1>
        <p className="text-text-secondary text-lg max-w-2xl mx-auto mb-8">
          Scegli il piano giusto per la tua struttura sanitaria. Nessun costo nascosto, cancella quando vuoi.
        </p>

        {/* Toggle */}
        <div className="inline-flex items-center gap-3 bg-surface-elevated border border-border rounded-full p-1">
          <button onClick={() => setPeriodo('mensile')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${periodo === 'mensile' ? 'bg-sky-600 text-white' : 'text-muted-color hover:text-white'}`}>
            Mensile
          </button>
          <button onClick={() => setPeriodo('annuale')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${periodo === 'annuale' ? 'bg-sky-600 text-white' : 'text-muted-color hover:text-white'}`}>
            Annuale
            <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">-20%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {piani.map(piano => {
              const risparmioVal = risparmio(piano);
              return (
                <div key={piano.id}
                  className={`relative rounded-2xl border p-6 flex flex-col transition-all ${piano.evidenziato ? 'border-sky-500 bg-sky-500/5 shadow-lg shadow-sky-500/10' : 'border-border bg-surface-elevated'}`}>
                  {piano.evidenziato && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-sky-500 text-white text-xs font-bold px-4 py-1 rounded-full">PIÙ POPOLARE</span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-white mb-1">{piano.nome}</h3>
                    <p className="text-muted-color text-sm">{piano.descrizione}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold text-white">{formatPrezzo(piano)}</span>
                    </div>
                    {periodo === 'annuale' && risparmioVal && (
                      <p className="text-green-400 text-sm mt-1">Risparmi il {risparmioVal}% rispetto al mensile</p>
                    )}
                    {periodo === 'annuale' && piano.slug !== 'base' && (
                      <p className="text-muted-color text-xs mt-0.5">€{piano.prezzo_annuale}/anno fatturati annualmente</p>
                    )}
                  </div>

                  <div className="mb-6 space-y-2 flex-1">
                    {(piano.funzionalita || []).map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-color">{f}</span>
                      </div>
                    ))}
                  </div>

                  {piano.max_dispositivi && (
                    <p className="text-xs text-muted-color mb-4">
                      Fino a {piano.max_dispositivi} dispositivi · {piano.max_utenti} utenti
                    </p>
                  )}

                  <button
                    onClick={() => handleSelectPiano(piano)}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${piano.slug === 'base' ? 'bg-surface border border-border text-muted-color cursor-default' : piano.evidenziato ? 'bg-sky-600 hover:bg-sky-500 text-white' : 'bg-surface-elevated hover:bg-surface border border-border text-white hover:border-sky-500/50'}`}>
                    {piano.slug === 'base' ? 'Piano Attuale' : piano.slug === 'enterprise' ? 'Contattaci' : 'Inizia Ora'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* FAQ */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-white mb-8">Domande Frequenti</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-4xl mx-auto">
              {[
                { q: 'Posso cambiare piano in qualsiasi momento?', a: 'Sì, puoi fare upgrade o downgrade in qualsiasi momento. Le modifiche vengono applicate immediatamente.' },
                { q: 'Come funziona il piano gratuito?', a: 'Il piano Base è completamente gratuito per sempre, senza carta di credito richiesta. Fino a 50 dispositivi e 5 utenti.' },
                { q: 'I dati sono al sicuro?', a: 'Sì, tutti i dati sono crittografati e archiviati su server europei conformi GDPR.' },
                { q: 'Offrite supporto per la migrazione?', a: 'Per i piani Struttura ed Enterprise offriamo supporto dedicato per la migrazione dei dati.' },
              ].map((faq, i) => (
                <div key={i} className="bg-surface-elevated border border-border rounded-xl p-4">
                  <p className="font-semibold text-white mb-2">{faq.q}</p>
                  <p className="text-sm text-muted-color">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && selectedPiano && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md">
            {success ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckIcon" size={32} className="text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Richiesta Inviata!</h3>
                <p className="text-muted-color text-sm mb-6">
                  {selectedPiano.slug === 'enterprise' ?'Il nostro team ti contatterà entro 24 ore per configurare il piano Enterprise.' :'Il tuo abbonamento è stato attivato con successo.'}
                </p>
                <button onClick={() => { setShowCheckout(false); setSuccess(false); }}
                  className="bg-sky-600 hover:bg-sky-500 text-white px-6 py-2 rounded-lg text-sm font-semibold">
                  Chiudi
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <h2 className="text-lg font-bold text-white">
                      {selectedPiano.slug === 'enterprise' ? 'Richiedi Piano Enterprise' : `Attiva Piano ${selectedPiano.nome}`}
                    </h2>
                    <p className="text-sm text-muted-color">{formatPrezzo(selectedPiano)}</p>
                  </div>
                  <button onClick={() => setShowCheckout(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors">
                    <Icon name="XMarkIcon" size={20} />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  {[
                    { label: 'Nome e Cognome *', key: 'nome', placeholder: 'Mario Rossi' },
                    { label: 'Email *', key: 'email', placeholder: 'mario@ospedale.it', type: 'email' },
                    { label: 'Nome Struttura', key: 'struttura', placeholder: 'Ospedale San Giovanni' },
                    { label: 'Telefono', key: 'telefono', placeholder: '+39 02 1234567' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-sm text-muted-color mb-1">{f.label}</label>
                      <input
                        type={f.type || 'text'}
                        value={(checkoutForm as any)[f.key]}
                        onChange={e => setCheckoutForm(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white"
                        placeholder={f.placeholder}
                      />
                    </div>
                  ))}
                  {selectedPiano.slug !== 'enterprise' && (
                    <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 text-xs text-sky-400">
                      <Icon name="LockClosedIcon" size={12} className="inline mr-1" />
                      Pagamento sicuro tramite Stripe. I tuoi dati sono protetti.
                    </div>
                  )}
                </div>
                <div className="flex gap-3 p-6 border-t border-border">
                  <button onClick={() => setShowCheckout(false)} className="flex-1 bg-surface-elevated border border-border text-white px-4 py-2 rounded-lg text-sm font-semibold">Annulla</button>
                  <button onClick={handleCheckout} disabled={submitting || !checkoutForm.nome || !checkoutForm.email}
                    className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    {submitting ? 'Elaborazione...' : selectedPiano.slug === 'enterprise' ? 'Invia Richiesta' : 'Procedi al Pagamento'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
